import { useTranslation } from 'react-i18next'
import { useState } from 'react'

import { CancelDisbursementBatchModal } from '../../cancelDisbursementBatchModal/CancelDisbursementBatchModal'
import { RenameDisbursementBatchModal } from '../../renameDisbursementBatchModal/RenameDisbursementBatchModal'

import {
  DetailedDisbursementsCount,
  DisbursementPaymentMethod
} from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'
import { FoundationNameWithFlag } from '@percent/admin-dashboard/common/components'
import { useFoundations, usePermissions } from '@percent/admin-dashboard/common/hooks'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { ButtonText, CardContent, CardDashboard, Copy, Flex, Button } from '@percent/lemonade'
import { DisbursementFoundationDetailsProps } from './DisbursementFoundationDetails.types'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'

const apiDisbursementBatchTypeToDisplayName: Record<DisbursementPaymentMethod, string> = {
  bank_transfer: 'Bank transfer',
  card: 'Card',
  cheque: 'Cheque'
}

export const getDisbursementBatchType = (detailedCounts: DetailedDisbursementsCount[]) => {
  const disbursementBatchTypeCount = detailedCounts.reduce(
    (acc, item) => {
      acc[item.type] += item.count

      return acc
    },
    {
      bank_transfer: 0,
      card: 0,
      cheque: 0
    }
  )

  return Object.entries(disbursementBatchTypeCount)
    .sort((a, b) => b[1] - a[1])
    .filter(count => count[1])
    .map(count => apiDisbursementBatchTypeToDisplayName[count[0] as DisbursementPaymentMethod])
    .join('/')
}

export function DisbursementFoundationDetails({
  id,
  foundationId,
  endDate,
  detailedCounts,
  counts,
  organisationCount,
  name,
  refreshData
}: Readonly<DisbursementFoundationDetailsProps>) {
  const { t } = useTranslation()
  const { foundations } = useFoundations()
  const { userHasPermissions } = usePermissions()
  const internalFgaDoraDisbursementsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_DISBURSEMENTS
  )

  const [isCancelBatchModalOpen, setIsCancelBatchModalOpen] = useState(false)
  const [isRenameBatchModalOpen, setIsRenameBatchModalOpen] = useState(false)

  const shouldShowCancelBatchButton = counts.total !== counts.paid + counts.cancelled
  const canCancelBatch = !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_cancel')
  const canRenameBatch = !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_update')

  return (
    <>
      <CardDashboard
        title={t('typography.disbursementBatchDetails')}
        action={
          shouldShowCancelBatchButton ? (
            <Button
              disabled={!canCancelBatch}
              variant="secondary"
              size="small"
              onClick={() => setIsCancelBatchModalOpen(true)}
            >
              {t(`button.cancelBatch`)}
            </Button>
          ) : null
        }
      >
        {/* Always rendered: an unnamed batch still needs the Rename action, which lives here. */}
        <CardContent title={t('typography.name')} testId="disbursement-batch-details-name">
          <Flex justify="space-between" align="center">
            <span>{name || '-'}</span>
            {canRenameBatch && (
              <ButtonText icon="edit" onClick={() => setIsRenameBatchModalOpen(true)} data-testid="rename-batch-button">
                {t('button.rename')}
              </ButtonText>
            )}
          </Flex>
        </CardContent>
        <CardContent title={t('typography.disbursementBatchId')} testId="disbursement-batch-details-id">
          <Flex justify="space-between" align="center">
            <span>{id}</span>
            <Copy text={id} btnLabel={t('typography.copy')} withIcon={false} variant="text" />
          </Flex>
        </CardContent>
        <CardContent title={t('typography.foundation')} testId="disbursement-batch-details-foundation">
          {foundations?.[foundationId] ? (
            <FoundationNameWithFlag
              countryCode={foundations[foundationId].countryCode}
              name={foundations[foundationId].name}
            />
          ) : (
            foundationId
          )}
        </CardContent>
        <CardContent title={t('typography.donationsUpTo')} testId="disbursement-batch-details-end-date">
          {dayJS(endDate).format('DD MMM YYYY')}
        </CardContent>
        <CardContent title={t('typography.disbursementBatchType')} testId="disbursement-batch-details-type">
          {getDisbursementBatchType(detailedCounts)}
        </CardContent>
        <CardContent
          title={t('typography.disbursementBatchUniqueOrganisations')}
          testId="disbursement-batch-details-unique-organisations"
        >
          {organisationCount}
        </CardContent>
      </CardDashboard>
      {isRenameBatchModalOpen && (
        <RenameDisbursementBatchModal
          isOpen={isRenameBatchModalOpen}
          onClose={() => setIsRenameBatchModalOpen(false)}
          disbursementBatchId={id}
          name={name}
          onSuccess={refreshData}
        />
      )}
      {isCancelBatchModalOpen && (
        <CancelDisbursementBatchModal
          isOpen={isCancelBatchModalOpen}
          onClose={() => setIsCancelBatchModalOpen(false)}
          disbursementBatchId={id}
          foundationId={foundationId}
          endDate={endDate}
          detailedCounts={detailedCounts}
          refreshData={refreshData}
        />
      )}
    </>
  )
}
